/**
 * Предохранитель от петли «callback без PKCE-verifier».
 *
 * С 0.12 либа бросает `MissingVerifierError`, когда для `state` из адреса нет
 * записи `sso_pkce:<state>` ни в sessionStorage, ни в localStorage (код при
 * этом НЕ сожжён — обмена не было). Один автоповтор входа почти всегда лечит:
 * запись потерялась вместе с исходной вкладкой. Но если браузер не хранит её
 * вовсе (ITP, «блокировать данные сайтов»), повтор даст ту же ошибку — без
 * предохранителя получится карусель NET → auth → NET. У NET, в отличие от Desk
 * с его `SSO_ATTEMPT_KEY`, своего предохранителя не было — это он.
 *
 * Метка живёт в sessionStorage: петля идёт top-level переходами внутри ОДНОЙ
 * вкладки, tab-scope достаточно и не блокирует соседние. Если sessionStorage
 * запрещён — localStorage (тогда блокировка общая: ошибиться в сторону экрана
 * ошибки безопаснее, чем в сторону петли).
 *
 * ⚠ Ключ намеренно НЕ начинается с `sso_pkce:` — те ключи принадлежат либе,
 * их при выходе не чистим и не трогаем.
 */
const KEY = "net_sso_verifier_retry_at";

/** Полный круг NET → auth → NET при живой SSO-cookie — 1–3 с; 30 с гасят карусель. */
const WINDOW_MS = 30_000;

/**
 * Повтор уже начат в ЭТОМ документе. StrictMode вызывает effect дважды, а либа
 * отдаёт на тот же код тот же отклонённый промис — решение должно приниматься
 * один раз, иначе вторая копия мигнёт экраном ошибки.
 */
let startedHere = false;

export type RetryDecision =
  | "retry" // можно повторить, метка поставлена
  | "pending" // повтор уже идёт в этом документе — оставить «Входим…»
  | "blocked"; // повтор уже был и не помог — экран ошибки

function stores(): Storage[] {
  const out: Storage[] = [];
  for (const name of ["sessionStorage", "localStorage"] as const) {
    try {
      const s = window[name];
      if (s) out.push(s);
    } catch {
      /* запрещено политикой браузера */
    }
  }
  return out;
}

export function consumeVerifierRetry(): RetryDecision {
  if (startedHere) return "pending";
  for (const s of stores()) {
    try {
      const raw = s.getItem(KEY);
      const at = raw === null ? NaN : Number(raw);
      // Отрицательная разница (часы перевели назад) — тоже «свежая метка»:
      // ошибаемся в сторону экрана ошибки, а не петли.
      if (Number.isFinite(at) && Date.now() - at < WINDOW_MS) return "blocked";
    } catch {
      /* ignore */
    }
  }
  const value = String(Date.now());
  for (const s of stores()) {
    try {
      s.setItem(KEY, value);
      startedHere = true;
      return "retry";
    } catch {
      /* пробуем следующее хранилище */
    }
  }
  // Метку писать некуда — повторять нельзя: второй провал ушёл бы в петлю.
  // (В этом же случае `startLogin` бросит `StorageUnavailableError`.)
  return "blocked";
}

/** Обмен кода удался — предохранитель больше не нужен. */
export function clearVerifierRetry(): void {
  for (const s of stores()) {
    try {
      s.removeItem(KEY);
    } catch {
      /* ignore */
    }
  }
}
